import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
export const spent = (a) => (a.expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0);

function NewAward({ open, setOpen }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ title: "", funder: "", amount_awarded: "", start_date: "", end_date: "", report_frequency: "quarterly" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/awards", { ...f, amount_awarded: Number(f.amount_awarded || 0) });
      toast.success("Award added — report deadlines were added to your calendar");
      navigate(`/dashboard/awards/${data.award_id}`);
    } catch (e2) { toast.error(formatErr(e2)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-award-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Log a grant award</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Grant / award name</Label><Input required className="mt-1.5" value={f.title} onChange={set("title")} data-testid="award-title-input" /></div>
          <div><Label>Funder</Label><Input className="mt-1.5" value={f.funder} onChange={set("funder")} data-testid="award-funder-input" /></div>
          <div><Label>Amount awarded ($)</Label><Input type="number" min="0" className="mt-1.5" value={f.amount_awarded} onChange={set("amount_awarded")} data-testid="award-amount-input" /></div>
          <div><Label>Period start</Label><Input required type="date" className="mt-1.5" value={f.start_date} onChange={set("start_date")} data-testid="award-start-input" /></div>
          <div><Label>Period end</Label><Input type="date" className="mt-1.5" value={f.end_date} onChange={set("end_date")} data-testid="award-end-input" /></div>
          <div className="col-span-2"><Label>Progress reports due</Label>
            <Select value={f.report_frequency} onValueChange={(v) => setF({ ...f, report_frequency: v })}>
              <SelectTrigger className="mt-1.5" data-testid="award-report-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="yearly">Yearly</SelectItem><SelectItem value="none">No recurring reports</SelectItem></SelectContent>
            </Select>
          </div>
          <Button type="submit" className="col-span-2 bg-emerald-600 hover:bg-emerald-700" data-testid="award-create-submit-button">Add award</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Awards() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/awards").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <PageHeader eyebrow="Post-award tracking" title="Awards & milestones"
        action={<Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="new-award-button"><Plus className="h-4 w-4 mr-2" />Log an award</Button>}>
        Track how grant funds are used and keep every milestone on schedule so you stay compliant and keep your funding.
      </PageHeader>
      {!items ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="awards-empty"><Trophy className="h-8 w-8 mx-auto text-amber-500" /><p className="mt-4 text-slate-500">Won a grant? Log it here to track spending, milestones and report deadlines.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((a) => {
            const s = spent(a);
            const pct = a.amount_awarded ? Math.min(100, Math.round((s / a.amount_awarded) * 100)) : 0;
            const done = (a.milestones || []).filter((m) => m.status === "done").length;
            return (
              <button key={a.award_id} onClick={() => navigate(`/dashboard/awards/${a.award_id}`)} data-testid={`award-card-${a.award_id}`} className="card-lift text-left bg-white border border-slate-200 rounded-lg p-6">
                <Trophy className="h-5 w-5 text-amber-500" />
                <h3 className="font-display text-xl font-bold text-slate-900 mt-3">{a.title}</h3>
                <p className="text-sm text-slate-500">{a.funder}</p>
                <div className="mt-5 flex justify-between text-xs text-slate-500"><span>{money(s)} spent of {money(a.amount_awarded)}</span><span>{pct}%</span></div>
                <Progress value={pct} className="h-1.5 mt-1.5" />
                <div className="text-xs text-slate-500 mt-3">{done}/{(a.milestones || []).length} milestones done</div>
              </button>
            );
          })}
        </div>
      )}
      <NewAward open={open} setOpen={setOpen} />
    </div>
  );
}
